import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForgotPassword } from '../../hooks/useForgotPassword';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FormErrorBanner } from '../../components/shared/FormErrorBanner';

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { email, setEmail, fieldErrors, formErrors, handleSubmit, loading, submitted } = useForgotPassword();

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50";
  const labelClass = "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block";

  const renderFieldError = (errors: string[]) => {
    if (errors.length === 0) {
      return null;
    }

    return <p className="mt-2 text-sm text-destructive">{t(errors[0])}</p>;
  };

  return (
    <div className="max-w-md mx-auto p-6 mt-10">
      <Card>
        <CardHeader>
          <h3 className="text-2xl leading-none tracking-tight">{t('auth.forgot_password.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('auth.forgot_password.subtitle')}</p>
        </CardHeader>
        <CardContent>
          <FormErrorBanner errors={formErrors} className="mb-4" />

          {submitted ? (
            <div className="bg-green-50 text-green-900 px-4 py-3 rounded-md text-sm">
              {t('auth.forgot_password.success')}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className={labelClass}>{t('auth.forgot_password.email_label')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className={inputClass}
                  placeholder={t('auth.forgot_password.email_placeholder')}
                  required
                  autoFocus
                />
                {renderFieldError(fieldErrors.email)}
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t('common.loading') : t('auth.forgot_password.submit')}
              </Button>
            </form>
          )}

          <div className="text-center text-sm mt-4">
            <a href="/login" className="text-foreground underline hover:no-underline font-medium">
              {t('auth.forgot_password.back_to_login')}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
